import { faDownload, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
} from 'react';
import { css } from 'styled-components';
import type { ActionMode } from 'src/components/action-mode';
import { useActionMode } from 'src/components/action-mode';
import type { ActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import { useActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import { focusStyleClasses } from 'src/styles';
import { cn } from 'src/utils/cn';

export type Size = 'default' | 'small';

export type ModeButtonProps =
  | {
      mode:
        | 'inverse' // maybe we can delete this when we delete the legacy styles (only used in menu)
        | 'icon'
        | 'icon-inverse'
        | 'embedded' // maybe we can delete this when we delete the legacy styles (only used in toast)
        | 'dropdown';
    }
  | {
      mode?: 'link' | 'pill';
      isActive?: boolean;
    }
  | {
      mode: 'link-underlined';
      icon?: 'external' | 'download';
    }
  | {
      mode?: 'primary';
      size?: Size;
    }
  | {
      mode: 'secondary';
      size?: Size;
      isActive?: boolean;
    }
  | {
      mode: 'link-primary';
    };

export type ButtonProps = {
  /**
   * Configure your button type.
   *
   * @default 'button'
   */
  type?: string;
} & ModeButtonProps &
  ComponentPropsWithoutRef<'button'>;

// helper type to allow easier access to the custom props
type CustomButtonProps = {
  mode: ModeButtonProps['mode'];
  icon?: 'external' | 'download';
  size?: Size;
  isActive?: boolean;
};

/**
 * Your basic button component. Use this for `onClick` interactions.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (baseProps, ref) => {
    const actionMode = useActionMode();
    const actionGroupMode = useActionGroupMode();

    // descruct all custom props
    const { mode, icon, size, isActive, ...props } = {
      type: 'button',
      ...mapButtonActionMode(actionMode),
      ...baseProps,
    } as ButtonProps & CustomButtonProps;

    switch (mode) {
      case 'link-underlined': {
        const classes = cn(
          getBaseButtonClasses({ isActive }),
          `
            border-none bg-transparent text-left leading-[inherit]
            text-legacy-brand underline decoration-legacy-brand-light-2
            hover:text-legacy-brand-secondary
            focus:text-legacy-brand-secondary
          `
        );
        const appendix = icon ? (
          <>
            {' '}
            <FontAwesomeIcon
              icon={icon === 'download' ? faDownload : faExternalLink}
            />
          </>
        ) : null;
        return appendix ? (
          <span>
            <button
              {...props}
              className={cn(classes, props.className)}
              ref={ref}
            />
            {appendix}
          </span>
        ) : (
          <button
            {...props}
            className={cn(classes, props.className)}
            ref={ref}
          />
        );
      }
      case 'link':
        return (
          <button
            {...props}
            className={cn(
              getBaseButtonClasses({ isActive }),
              `
                border-b-2 border-transparent bg-transparent text-left
                leading-[inherit] text-legacy-brand
              `,
              isActive && 'border-b-legacy-brand',
              'disabled:text-legacy-inactive disabled:opacity-100',
              !isActive &&
                !props.disabled &&
                `
                  hover:text-legacy-brand-secondary
                  focus:text-legacy-brand-secondary
                `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'pill':
        return (
          <button
            {...props}
            className={cn(
              getBaseButtonClasses({ isActive }),
              `
                border-b-2 border-none border-transparent bg-transparent
                text-left text-legacy-brand
              `,
              isActive && 'border-b-legacy-brand',
              `
                hover:border-b-legacy-brand hover:text-legacy-brand
                focus:border-b-legacy-brand focus:text-legacy-brand
              `,
              !isActive && props.disabled && 'text-legacy-inactive opacity-100',
              props.className
            )}
            ref={ref}
            disabled={props.disabled ?? isActive}
          />
        );
      case 'link-primary':
        return (
          <button
            {...props}
            className={cn(
              getBaseButtonClasses({ isActive }),
              `
                border-b-2 border-none border-transparent bg-transparent
                text-left text-white
              `,
              !props.disabled &&
                `
                  hover:border-b-white
                  focus:border-b-white
                `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'embedded':
        return (
          <button
            {...props}
            className={cn(
              getBaseButtonClasses({ isActive }),
              `
                border-none bg-none text-legacy-brand opacity-40
                transition-opacity duration-200
              `,
              !props.disabled &&
                `
                  hover:opacity-80
                  focus:opacity-80
                `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'icon':
        return (
          <button
            {...props}
            className={cn(
              getBaseButtonClasses({ isActive }),
              `
                border-none bg-transparent text-label-base
                text-foreground-secondary-default
                not-disabled:not-active:hover:text-foreground-secondary-hover
                active:text-foreground-secondary-active
                disabled:text-foreground-secondary-disabled
              `,
              focusStyleClasses,
              `
                legacy:text-[1.6rem] legacy:text-legacy-brand
                legacy:not-disabled:not-active:hover:text-legacy-brand-secondary
                legacy:not-disabled:not-active:focus:text-legacy-brand-secondary
                legacy:active:text-legacy-brand-secondary
                legacy:disabled:text-legacy-inactive
              `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'icon-inverse':
        return (
          <button
            {...props}
            className={cn(
              getBaseButtonClasses({ isActive }),
              `
                border-none bg-transparent text-label-base
                text-foreground-primary-default
                not-disabled:not-active:hover:text-background-secondary-default
                active:text-foreground-secondary-active
                disabled:text-foreground-secondary-disabled
              `,
              focusStyleClasses,
              `
                legacy:bg-legacy-brand legacy:text-[1.6rem] legacy:text-white
                legacy:not-disabled:not-active:hover:text-legacy-brand-secondary
                legacy:not-disabled:not-active:focus:text-legacy-brand-secondary
                legacy:active:text-legacy-brand-secondary
                legacy:disabled:text-legacy-inactive
              `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'dropdown':
        return (
          <button
            {...props}
            className={getButtonDropdownClasses({
              className: props.className,
            })}
            ref={ref}
          />
        );
      case 'inverse':
        return (
          <button
            {...props}
            className={getButtonInverseClasses({
              actionGroupMode,
              className: props.className,
            })}
            ref={ref}
          />
        );
      case 'secondary':
        return (
          <button
            {...props}
            className={getButtonSecondaryClasses({
              isActive,
              actionGroupMode,
              size,
              className: props.className,
            })}
            ref={ref}
            disabled={props.disabled ?? isActive}
          />
        );
      case 'primary':
      case undefined:
        return (
          <button
            {...props}
            className={getButtonPrimaryClasses({
              actionGroupMode,
              size,
              className: props.className,
            })}
            ref={ref}
          />
        );
    }
  }
);

export function getButtonDropdownClasses({
  className,
}: {
  className?: string;
}) {
  return cn(
    getBaseButtonClasses({
      isActive: undefined,
    }),
    `
      inline-block w-full rounded-xs bg-background-surface-default px-l py-s
      text-left text-label-base text-foreground-default
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-surface-hover
      active:bg-background-surface-selected
      active:text-foreground-surface-selected
      disabled:bg-background-surface-disabled
      disabled:text-foreground-surface-disabled
    `,
    focusStyleClasses,
    `
      legacy:rounded-none legacy:bg-transparent legacy:px-[2rem]
      legacy:py-[0.2rem] legacy:text-legacy-size-base legacy:text-[#333]
      legacy:not-disabled:not-active:hover:bg-[#f5f5f5]
      legacy:not-disabled:not-active:hover:text-[#333]
      legacy:not-disabled:not-active:focus:bg-[#f5f5f5]
      legacy:not-disabled:not-active:focus:text-[#333]
      legacy:active:bg-[#f5f5f5] legacy:active:text-[#333]
    `,
    className
  );
}

export function getButtonInverseClasses({
  actionGroupMode,
  className,
}: {
  actionGroupMode: ActionGroupMode;
  className?: string;
}) {
  return cn(
    getBaseButtonClasses({ isActive: undefined }),
    getActionGroupModeClasses({ actionGroupMode, size: undefined }),
    `
      bg-background-primary-inverted text-center font-label-semibold
      text-foreground-primary-inverted
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-primary-inverted
      not-disabled:not-active:hover:text-foreground-secondary-hover
      active:bg-background-primary-inverted
      active:text-foreground-secondary-active
      disabled:bg-background-primary-inverted
      disabled:text-foreground-primary-disabled
    `,
    focusStyleClasses,
    getActionSizeClasses({ size: undefined }),
    `
      legacy:border-2 legacy:border-white legacy:bg-white
      legacy:font-legacy-bold legacy:text-legacy-brand
      legacy:not-disabled:not-active:hover:border-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:text-white
      legacy:not-disabled:not-active:focus:border-legacy-brand-secondary
      legacy:not-disabled:not-active:focus:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:focus:text-white
      legacy:active:border-legacy-brand-secondary
      legacy:active:bg-legacy-brand-secondary legacy:active:text-white
      legacy:active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
    `,
    className
  );
}

export function getButtonSecondaryClasses({
  isActive,
  actionGroupMode,
  size,
  className,
}: {
  isActive?: boolean;
  actionGroupMode: ActionGroupMode;
  size?: Size;
  className?: string;
}) {
  return cn(
    getBaseButtonClasses({ isActive }),
    getActionGroupModeClasses({ actionGroupMode, size }),
    `
      bg-background-secondary-default text-center font-label-semibold
      text-foreground-secondary-default
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-secondary-hover
      not-disabled:not-active:hover:text-foreground-secondary-hover
      active:bg-background-secondary-active
      active:text-foreground-secondary-active
      disabled:bg-background-secondary-disabled
      disabled:text-foreground-secondary-disabled
    `,
    focusStyleClasses,
    getActionSizeClasses({ size }),
    `
      legacy:border-2 legacy:border-transparent legacy:bg-legacy-brand-light-3
      legacy:font-legacy-bold legacy:text-legacy-brand
      legacy:not-disabled:not-active:hover:border-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:text-white
      legacy:not-disabled:focus:border-legacy-brand-secondary
      legacy:not-disabled:focus:bg-legacy-brand-secondary
      legacy:not-disabled:focus:text-white
      legacy:active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
      legacy:disabled:bg-legacy-brand-light-3 legacy:disabled:text-legacy-brand
    `,
    isActive && 'legacy:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]',
    className
  );
}

export function getButtonPrimaryClasses({
  actionGroupMode,
  size,
  className,
}: {
  actionGroupMode: ActionGroupMode;
  size?: Size;
  className?: string;
}) {
  return cn(
    getBaseButtonClasses({ isActive: undefined }),
    getActionGroupModeClasses({ actionGroupMode, size }),
    `
      bg-background-primary-default text-center font-label-semibold
      text-foreground-primary-default
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-primary-hover
      not-disabled:not-active:hover:text-foreground-primary-hover
      active:bg-background-primary-active active:text-foreground-primary-active
      disabled:bg-background-primary-disabled
      disabled:text-foreground-primary-default
    `,
    focusStyleClasses,
    getActionSizeClasses({ size }),
    `
      legacy:border-2 legacy:border-legacy-brand legacy:bg-legacy-brand
      legacy:font-legacy-bold legacy:text-white
      legacy:not-disabled:not-active:hover:border-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:text-white
      legacy:not-disabled:focus:border-legacy-brand-secondary
      legacy:not-disabled:focus:bg-legacy-brand-secondary
      legacy:not-disabled:focus:text-white
      legacy:active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
      legacy:disabled:bg-legacy-brand legacy:disabled:text-white
    `,
    className
  );
}

export function getBaseButtonClasses({ isActive }: { isActive?: boolean }) {
  return cn(
    `
      cursor-pointer transition-colors duration-150 select-none
      legacy:duration-350
    `,
    !isActive &&
      `
        disabled:cursor-not-allowed
        legacy:disabled:opacity-65 legacy:disabled:shadow-none
      `,
    isActive && 'disabled:cursor-default'
  );
}

export function getActionGroupModeClasses({
  actionGroupMode,
  size,
}: {
  actionGroupMode: ActionGroupMode;
  size?: Size;
}) {
  return cn(
    actionGroupMode === 'left' &&
      size === 'small' &&
      `
        rounded-l-3xl
        legacy:rounded-l-[1.6rem]
      `,
    actionGroupMode === 'left' &&
      size !== 'small' &&
      `
        rounded-l-3xl
        legacy:rounded-l-[1.8rem]
      `,
    actionGroupMode === 'right' &&
      size === 'small' &&
      `
        rounded-r-3xl
        legacy:rounded-r-[1.6rem]
      `,
    actionGroupMode === 'right' &&
      size !== 'small' &&
      `
        rounded-r-3xl
        legacy:rounded-r-[1.8rem]
      `,
    actionGroupMode === 'single' &&
      size === 'small' &&
      `
        rounded-3xl
        legacy:rounded-[1.6rem]
      `,
    actionGroupMode === 'single' &&
      size !== 'small' &&
      `
        rounded-3xl
        legacy:rounded-[1.8rem]
      `,
    actionGroupMode === 'left' && 'mr-[0.1rem]',
    actionGroupMode === 'middle' && 'mr-[0.1rem]'
  );
}

export function getActionSizeClasses({ size }: { size?: Size }) {
  return cn(
    size === 'small'
      ? `
        px-l py-xs text-label-s
        legacy:px-[1rem] legacy:py-[0.4rem] legacy:text-legacy-size-xs
        legacy:leading-none
      `
      : `
        px-xl py-m text-label-base
        legacy:px-[2rem] legacy:py-[0.6rem] legacy:text-legacy-size-base
      `
  );
}

export function mapButtonActionMode(
  actionMode: ActionMode | null
): ModeButtonProps | null {
  if (!actionMode) return null;
  if (actionMode.mode === 'button-primary') {
    return { ...actionMode, mode: 'primary' as const };
  }
  if (actionMode.mode === 'button-secondary') {
    return { ...actionMode, mode: 'secondary' as const };
  }
  return actionMode as ModeButtonProps;
}

/** @deprecated Old styled-components styles. */
export const buttonTransition = css`
  transition:
    color 0.35s,
    background-color 0.35s,
    border-color 0.35s;
`;

/** @deprecated Old styled-components styles. */
export const buttonBaseStyle = css<
  ButtonHTMLAttributes<HTMLButtonElement> & { isActive?: boolean }
>`
  cursor: pointer;
  user-select: none;
  ${buttonTransition};

  ${({ disabled, isActive = false }) =>
    disabled &&
    !isActive &&
    css`
      cursor: not-allowed;
      opacity: 0.65;
      box-shadow: none;
    `}

  ${({ disabled, isActive = false }) =>
    disabled &&
    isActive &&
    css`
      cursor: default;
    `}
`;
